import {
  Avatar,
  BoxProps,
  Flex,
  Heading,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useTranslation } from 'next-i18next'
import React, { FC, useContext } from 'react'
import useSWR from 'swr'

import { createV3Client } from '@miimosa/api/v3/base'
import type { PublicUser } from '@miimosa/common/types'
import CollapseArrow from '@miimosa/design-system/components/icons/CollapseArrow'
import { Notifier } from '@miimosa/protocol'

import { WalletContext } from '@components/WalletProvider'

import { LoggedItems } from './LoggedItems'

const Nav = ({
  user,
  onClose,
  unreadNotificationsCount,
}: {
  user: PublicUser
  onClose: () => void
  unreadNotificationsCount?: Notifier.CountUnreadNotificationsResponse
}) => {
  const { initials, avatar, full_name, email } = user

  return (
    <>
      <Flex direction="row" alignItems="center" pl="24px" py="24px" bg="dark_green" w="full">
        <Avatar size="xl" name={initials} src={avatar ?? undefined} />
        <Flex ml="12px" direction="column">
          <Heading size="xs" fontWeight="400" color="white">
            {full_name}
          </Heading>
          <Text size="md" color="white">
            {email}
          </Text>
        </Flex>
      </Flex>
      <LoggedItems user={user} onClose={onClose} unreadNotificationsCount={unreadNotificationsCount} />
    </>
  )
}

interface Props {
  user?: PublicUser
}

const HeaderAvatar: FC<Props & BoxProps> = ({ user, ...props }) => {
  const { t } = useTranslation()
  const wallet = useContext(WalletContext)
  const isMobile = useBreakpointValue({ base: true, lg: false }, { fallback: 'lg' })

  const client = createV3Client()
  const fetcher = (url: string) => client.post(url, {}).then((res) => res.data)
  const { data: unreadNotificationsCount } = useSWR<Notifier.CountUnreadNotificationsResponse>(
    user ? '/notifier/v1/CountUnreadNotifications' : null,
    fetcher,
    {
      refreshInterval: 5 * 60000, // milliseconds aka 5 min
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  )
  if (!user) return null

  return (
    <Flex ml="24px" {...props}>
      <Popover closeOnBlur={true} margin-top="-3px" gutter={16} placement="bottom-end">
        {({ isOpen, onClose }) => (
          <>
            <PopoverTrigger>
              <Flex
                direction="row"
                columnGap="2"
                alignItems="center"
                cursor="pointer"
                as="button"
                sx={{
                  _hover: {
                    svg: {
                      bg: 'lighten',
                    },
                  },
                }}
              >
                {!isMobile &&
                  (wallet && wallet.amount >= 0 ? (
                    <Tooltip
                      label={`Mis à jour à ${new Date(wallet.amount_synced_at).toLocaleString('fr-FR', {
                        hour: 'numeric',
                        minute: 'numeric',
                      })} le ${new Date(wallet.amount_synced_at).toLocaleString('fr-FR', {
                        timeZone: 'UTC',
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      })}`}
                    >
                      <Flex direction="column" alignItems="flex-start">
                        <Text size="xs">{t('header.available_wallet_amount_2')}</Text>
                        <Text size="md">
                          {new Intl.NumberFormat('fr-FR', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 2,
                          }).format(wallet.amount / 100)}
                        </Text>
                      </Flex>
                    </Tooltip>
                  ) : (
                    <Flex direction="column" alignItems="flex-start">
                      <Text size="xs">{t('header.available_wallet_amount_2')}</Text>
                      <Text size="md">
                        {new Intl.NumberFormat('fr-FR', {
                          style: 'currency',
                          currency: 'EUR',
                          maximumFractionDigits: 2,
                        }).format(0)}
                      </Text>
                    </Flex>
                  ))}
                <Flex
                  position="relative"
                  alignItems={'center'}
                  {...(unreadNotificationsCount?.count && {
                    _after: {
                      content: '""',
                      bg: 'orange',
                      borderRadius: '50%',
                      height: '0.5em',
                      width: '0.5em',
                      display: 'block',
                      backgroundSize: 'contain',
                      position: 'absolute',
                      top: '0',
                      right: '0',
                    },
                  })}
                >
                  <Avatar size="md" name={user.initials} src={user.avatar ?? undefined} />
                </Flex>
                <CollapseArrow
                  fill="dark_green"
                  transition="transform 0.2s linear"
                  transform={isOpen ? 'rotate(180deg)' : ''}
                  w="12px"
                  h="12px"
                  py="4px"
                  px="2px"
                  borderRadius="full"
                />
              </Flex>
            </PopoverTrigger>

            <PopoverContent
              bg="white"
              border=""
              display="flex"
              flexDirection="column"
              alignItems="start"
              boxShadow="0 0 10px 0 rgba(0,0,0,0.06)"
              px="0"
              rounded="none"
              w="376px"
              maxH="472px"
              overflow="hidden"
              overflowY="auto"
              _focus={{
                outline: 'none',
              }}
              _focusVisible={{
                outline: 'none',
                boxShadow: '0 0 10px 0 rgba(0,0,0,0.06)',
              }}
            >
              <Nav user={user} onClose={onClose} unreadNotificationsCount={unreadNotificationsCount} />
            </PopoverContent>
          </>
        )}
      </Popover>
    </Flex>
  )
}

export default HeaderAvatar
